'use client'

import {useCallback, useEffect, useRef, useState} from 'react'
import {useLocation} from 'react-router-dom'
import {useAuth} from 'src/contexts/AuthContext'
import {dismissPortalMigrationTour, fetchPortalMigrationTourStatus} from 'src/lib/portal-migration/fetch-portal-migration-tour-status'
import {isPostLoginDashboardPath} from 'src/lib/portal-migration/is-post-login-dashboard-path'
import {clearStashedPortalMigrationTourShouldShow, peekStashedPortalMigrationTourShouldShow} from 'src/lib/portal-migration/portal-migration-tour-session'

export function usePortalViewSwitcherOnboarding(enabled: boolean) {
  const {isAuth} = useAuth()
  const {pathname} = useLocation()
  const [visible, setVisible] = useState(false)
  const dismissedInSessionRef = useRef(false)

  useEffect(() => {
    if (!enabled || !isAuth || !isPostLoginDashboardPath(pathname)) {
      setVisible(false)
      return
    }

    let cancelled = false

    const stashed = peekStashedPortalMigrationTourShouldShow()
    if (stashed !== null) {
      if (stashed && !dismissedInSessionRef.current) setVisible(true)
      else setVisible(false)
      return
    }

    void fetchPortalMigrationTourStatus()
      .then(({shouldShowPortalMigrationTour}) => {
        if (cancelled || dismissedInSessionRef.current) return
        setVisible(shouldShowPortalMigrationTour)
      })
      .catch(() => {
        if (!cancelled) setVisible(false)
      })

    return () => {
      cancelled = true
    }
  }, [enabled, isAuth, pathname])

  const dismiss = useCallback(() => {
    dismissedInSessionRef.current = true
    clearStashedPortalMigrationTourShouldShow()
    setVisible(false)
    void dismissPortalMigrationTour()
  }, [])

  return {visible, dismiss}
}
